import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  MIN_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import {
  TitleColumnFilter,
  useInfiniteTitles,
} from '@issuemanager/client/features/issues';
import { useState } from 'react';
import type { GroupedEntityTitleDto, MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type TitleMultipleSelectorProps = MultipleSelectorProps<
  GroupedEntityTitleDto[],
  GroupedEntityTitleDto
>;

export function TitleMultipleSelector(props: TitleMultipleSelectorProps) {
  const { value, onChange, enabled: _enabled, ...rest } = props;

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value ?? []);
  const issueTitlesQuery = useInfiniteTitles({
    filter: {
      searchTerm: filterByText,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
  });
  const actionTitlesQuery = useInfiniteTitles({
    filter: {
      searchTerm: filterByText,
      isActionTitle: true,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
  });

  const issueTitles =
    issueTitlesQuery.data?.pages.map((c) =>
      c.data.map((d) => ({
        ...d,
        id: d.title,
        group: TitleColumnFilter.ISSUE_TITLE,
      })),
    ) ?? [];

  const actionTitles =
    actionTitlesQuery.data?.pages.map((c) =>
      c.data.map((d) => ({
        ...d,
        id: d.title,
        group: TitleColumnFilter.ACTION_TITLE,
      })),
    ) ?? [];

  const options = mergePaginatedDataAndValue(
    issueTitles.concat(actionTitles),
    value ?? [],
    (option) => `${option.id}-${option.group}`,
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    loading: issueTitlesQuery.isLoading || actionTitlesQuery.isLoading,
    onScrollEnd: () => {
      issueTitlesQuery.fetchNextPage();
      actionTitlesQuery.fetchNextPage();
    },
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
    isOptionEqualToValue: (option, value) =>
      option.id === value.id && option.group === value.group,
  });

  const sortedOptions = sortOptionsBySelected(
    options,
    partialValue,
    autocompleteProps.isOptionEqualToValue,
  );

  const finalOptions = sortedOptions
    .filter((o) => o.group === TitleColumnFilter.ISSUE_TITLE)
    .concat(
      sortedOptions.filter((o) => o.group === TitleColumnFilter.ACTION_TITLE),
    );

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={finalOptions}
      getOptionKey={(option) => `${option.title}-${option.group}`}
      getOptionLabel={(option) => option.title}
      groupBy={(option) => option.group}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
